import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Ban, KeyRound, ScanSearch, ShieldCheck, Bot } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchApps, setAppStatus, DEV_STR as S, type AppRow } from './devApi'

/**
 * A13.01 — API-приложения (api_id apps) + A13.05 block/unblock. Dormant until
 * `/admin/developers/apps` ships. Placeholder permission USERS_VIEW.
 */
export function AppsScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<AppRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'api_id', header: S.apiId, type: 'number' },
      { key: 'owner', header: S.owner, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'requests_today', header: S.requestsToday, type: 'number' },
      { key: 'created_at', header: S.created, type: 'text' },
    ],
    [],
  )

  const changeStatus = useCallback(
    async (row: AppRow, status: string) => {
      try {
        await setAppStatus(row.id, status)
        toast(S.statusChanged, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<AppRow>[]>(
    () => [
      {
        id: 'block',
        label: S.block,
        icon: <Ban size={14} />,
        danger: true,
        onSelect: (r) => changeStatus(r, 'blocked'),
        visible: (r) => r.status === 'active',
      },
      {
        id: 'unblock',
        label: S.unblock,
        icon: <ShieldCheck size={14} />,
        onSelect: (r) => changeStatus(r, 'active'),
        visible: (r) => r.status === 'blocked',
      },
    ],
    [changeStatus],
  )

  const fetcher = useCallback(fetchApps, [])
  return (
    <>
      <PageHeader
        title={S.apps}
        screenId="A13.01"
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/developers/bot-tokens"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Bot size={13} />
              {S.botTokens}
            </Link>
            <Link
              to="/developers/leaked"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ScanSearch size={13} />
              {S.leaked}
            </Link>
          </div>
        }
      />
      <DataTable<AppRow>
        screenId="A13.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <KeyRound size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
